import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Factory as FactoryIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FullScreenLayout from './layouts/FullScreenLayout';
import { productionPlantsApi } from '../services/productionPlantsApi';

// Styled components
const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #e0e0e0',
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  backgroundColor: 
    status === 'pending' ? '#ff9800' :
    status === 'in_progress' ? '#2196f3' :
    status === 'completed' ? '#4caf50' :
    status === 'cancelled' ? '#f44336' :
    status === 'on_hold' ? '#9e9e9e' :
    '#757575',
  color: '#ffffff',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  marginBottom: theme.spacing(2),
}));

const ProductionBatchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: <WarningIcon />, color: '#ff9800' },
    { value: 'in_progress', label: 'In Progress', icon: <PlayArrowIcon />, color: '#2196f3' },
    { value: 'completed', label: 'Completed', icon: <CheckCircleIcon />, color: '#4caf50' },
    { value: 'cancelled', label: 'Cancelled', icon: <CancelIcon />, color: '#f44336' },
    { value: 'on_hold', label: 'On Hold', icon: <PauseIcon />, color: '#9e9e9e' }
  ];

  useEffect(() => {
    if (id) {
      fetchBatchDetails();
    }
  }, [id]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await productionPlantsApi.getBatch(id);
      setBatch(response.data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      showSnackbar('Failed to fetch batch details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await productionPlantsApi.updateBatchStatus(id, { status: newStatus });
      if (response.success) {
        showSnackbar(`Batch status updated to ${newStatus}`);
        fetchBatchDetails();
      }
    } catch (error) {
      console.error('Error updating batch status:', error);
      showSnackbar('Failed to update batch status', 'error');
    }
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.icon : <WarningIcon />;
  };

  const getItemTypeColor = (itemType) => {
    switch (itemType) {
      case 'input': return '#2196f3';
      case 'output': return '#4caf50';
      case 'byproduct': return '#ff9800';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#000000' }} />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="text.secondary">
          Batch not found
        </Typography>
      </Box>
    );
  }

  const header = (
    <HeaderContainer>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={() => navigate('/production-batches')}
          sx={{ color: '#000000' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2} mb={0.5}>
            <Typography variant="h4" fontWeight={700} color="#000000">
              {batch.batch.batch_number}
            </Typography>
            <StyledChip
              label={batch.batch.status}
              status={batch.batch.status}
              icon={getStatusIcon(batch.batch.status)}
              size="small"
            />
          </Box>
          <Box display="flex" alignItems="center" gap={3}>
            <Typography variant="body2" color="text.secondary">
              Plant: {batch.batch.plant_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quantity: {batch.batch.requested_quantity}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Items: {batch.items?.length || 0}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" gap={2}>
        {batch.batch.status === 'pending' && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => handleStatusUpdate('in_progress')}
            sx={{
              backgroundColor: '#2196f3',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#1976d2' },
            }}
          >
            Start Production
          </Button>
        )}
        {batch.batch.status === 'in_progress' && (
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleStatusUpdate('completed')}
            sx={{
              backgroundColor: '#4caf50',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#388e3c' },
            }}
          >
            Complete
          </Button>
        )}
      </Box>
    </HeaderContainer>
  );

  const mainContent = (
    <Box sx={{ p: 3 }}>
      {/* Batch Information */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <InfoCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Batch Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch.description || 'No description'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Plant Location
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch.plant_location || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Requested By
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch.requested_by_username || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch.created_by_username || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch.notes || 'No notes'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <InfoCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Timeline
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(batch.batch.created_at).toLocaleString()}
                  </Typography>
                </Box>
                {batch.batch.estimated_start_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Estimated Start
                    </Typography>
                    <Typography variant="body1">
                      {new Date(batch.batch.estimated_start_date).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {batch.batch.estimated_completion_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Estimated Completion
                    </Typography>
                    <Typography variant="body1">
                      {new Date(batch.batch.estimated_completion_date).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {batch.batch.actual_start_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Actual Start
                    </Typography>
                    <Typography variant="body1">
                      {new Date(batch.batch.actual_start_date).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {batch.batch.actual_completion_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Actual Completion
                    </Typography>
                    <Typography variant="body1">
                      {new Date(batch.batch.actual_completion_date).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Batch Items */}
      {batch.items && batch.items.length > 0 && (
        <InfoCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Batch Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Planned Qty</TableCell>
                    <TableCell align="right">Actual Qty</TableCell>
                    <TableCell>Section</TableCell>
                    <TableCell>Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.items.map((item) => (
                    <TableRow key={item.batch_item_id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {item.item_name || `Item ${item.item_id}`}
                          </Typography>
                          {item.item_description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.item_description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.item_type}
                          size="small"
                          sx={{
                            backgroundColor: getItemTypeColor(item.item_type),
                            color: '#ffffff',
                            fontWeight: 500,
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {item.planned_quantity}
                      </TableCell>
                      <TableCell align="right">
                        {item.actual_quantity}
                      </TableCell>
                      <TableCell>
                        {item.section_name || 'Not assigned'}
                      </TableCell>
                      <TableCell>
                        {item.item_unit || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </InfoCard>
      )}

      {/* Progress Tracking */}
      {batch.progress && batch.progress.length > 0 && (
        <InfoCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Production Progress
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Section</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.progress.map((progress) => (
                    <TableRow key={progress.progress_id}>
                      <TableCell>
                        {progress.item_name || `Item ${progress.item_id}`}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {progress.section_order}. {progress.section_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {progress.quantity}
                      </TableCell>
                      <TableCell>
                        {new Date(progress.updated_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {progress.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </InfoCard>
      )}

      {/* Empty state for no items */}
      {(!batch.items || batch.items.length === 0) && (
        <InfoCard>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <InventoryIcon sx={{ fontSize: '3rem', color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Items in Batch
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This batch doesn't have any items assigned yet.
              </Typography>
            </Box>
          </CardContent>
        </InfoCard>
      )}
    </Box>
  );

  return (
    <>
      <FullScreenLayout
        header={header}
        mainPanel={mainContent}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductionBatchDetails; 